import React, { useEffect, useRef, useState } from "react";
import { Table } from "@tanstack/react-table";
import "./TableColumnVisibilityMenu.scss";

interface TableColumnVisibilityMenuProps<T> {
    table: Table<T>;
    onReset?: () => void;
}

function TableColumnVisibilityMenu<T>({
    table,
    onReset,
}: TableColumnVisibilityMenuProps<T>): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const toggleMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleReset = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onReset) {
            onReset();
        }
        setIsOpen(false);
    };

    const getColumnLabel = (column: any): string => {
        const header = column.columnDef.header;
        if (typeof header === "string") {
            return header;
        }
        return column.id;
    };

    const visibleColumnsCount = table.getVisibleLeafColumns().length;

    return (
        <div className="table_column_visibility_menu" ref={menuRef}>
            <button
                className="table_column_visibility_menu__button"
                onClick={toggleMenu}
                type="button"
                aria-label="Toggle column visibility"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12.791 3.99805C12.8734 3.99805 12.9536 4.02413 13.0205 4.07227C13.0874 4.12048 13.138 4.18844 13.1641 4.26661L13.8447 6.3418C13.8664 6.40436 13.9039 6.46065 13.9531 6.50489C14.0024 6.54913 14.0624 6.57991 14.127 6.59473C14.3959 6.66105 14.6571 6.75677 14.9053 6.87989C14.9596 6.90813 15.0199 6.92286 15.0811 6.92286C15.1423 6.92284 15.2025 6.90818 15.2569 6.87989L17.0567 5.9336C17.1306 5.89486 17.2154 5.88157 17.2979 5.89454C17.3802 5.90749 17.4562 5.94646 17.5147 6.00587L18.5928 7.08399C18.6519 7.14328 18.6905 7.21999 18.7031 7.30274C18.7157 7.38559 18.7019 7.47068 18.6631 7.54493L17.7227 9.3418C17.6942 9.39734 17.6797 9.45911 17.6797 9.52149C17.6797 9.5839 17.6942 9.64563 17.7227 9.70118C17.8337 9.92294 17.9265 10.1535 17.999 10.3906C18.0163 10.4499 18.0476 10.5048 18.0899 10.5498C18.132 10.5947 18.1843 10.6294 18.2422 10.6504L20.2324 11.3418C20.3094 11.3688 20.3756 11.4198 20.4229 11.4863C20.4701 11.5529 20.4961 11.6322 20.4961 11.7139V13.2793C20.4961 13.3609 20.4701 13.4403 20.4229 13.5068C20.3756 13.5734 20.3094 13.6244 20.2324 13.6514L18.2569 14.3418C18.1955 14.3639 18.141 14.4015 18.0977 14.4502C18.0543 14.4991 18.0234 14.5584 18.0088 14.6221C17.9425 14.8925 17.8465 15.155 17.7227 15.4043C17.6945 15.459 17.6797 15.5196 17.6797 15.5811C17.6797 15.6425 17.6945 15.7032 17.7227 15.7578L18.6641 17.5576C18.7031 17.6318 18.7176 17.717 18.7051 17.7998C18.6925 17.8828 18.6531 17.9602 18.5938 18.0195L17.5156 19.0977C17.4563 19.1569 17.3798 19.1964 17.2969 19.209C17.214 19.2216 17.129 19.2069 17.0547 19.168L15.2256 18.209C15.1792 18.1842 15.1285 18.1683 15.0762 18.1631C15.0237 18.1579 14.9704 18.1633 14.9199 18.1787C14.6319 18.2717 14.3866 18.406 14.1016 18.501C14.0432 18.5183 13.9898 18.5492 13.9453 18.5908C13.9007 18.6327 13.8658 18.6848 13.8447 18.7422L13.1445 20.7422C13.1179 20.819 13.0684 20.8858 13.002 20.9326C12.9355 20.9794 12.8557 21.0037 12.7744 21.0029H11.21C11.1288 21.0013 11.0497 20.9749 10.9844 20.9268C10.9192 20.8786 10.8703 20.8113 10.8447 20.7344L10.1572 18.7568C10.1349 18.6957 10.0977 18.6408 10.0488 18.5977C9.99994 18.5545 9.94054 18.5243 9.87697 18.5098C9.6067 18.4432 9.3442 18.3471 9.09474 18.2236C9.04048 18.1955 8.98011 18.1807 8.91896 18.1807C8.85781 18.1807 8.79744 18.1955 8.74318 18.2236L6.94337 19.169C6.86957 19.2076 6.78447 19.222 6.70216 19.209C6.61999 19.196 6.54461 19.1561 6.48634 19.0967L5.40822 18.0205C5.34887 17.9612 5.30949 17.8837 5.29689 17.8008C5.28438 17.7179 5.29889 17.6328 5.33791 17.5586L6.29591 15.7305C6.32085 15.684 6.33666 15.6326 6.34181 15.5801C6.34696 15.5276 6.34162 15.4743 6.32619 15.4238C6.23223 15.136 6.09892 14.8913 6.00294 14.6065C5.98563 14.5487 5.95518 14.4953 5.91408 14.4512C5.8728 14.4069 5.82129 14.3719 5.76466 14.3506L3.76466 13.6514C3.68804 13.6246 3.62198 13.5741 3.57521 13.5078C3.52843 13.4413 3.5032 13.3616 3.50392 13.2803V11.7158C3.50381 11.6343 3.52911 11.5548 3.57619 11.4883C3.62346 11.4217 3.69046 11.3706 3.76759 11.3438L5.7588 10.6514C5.81678 10.6305 5.86895 10.5957 5.91115 10.5508C5.95338 10.5058 5.98478 10.4509 6.00197 10.3916C6.07449 10.1541 6.16726 9.92327 6.27834 9.70118C6.30699 9.64571 6.3213 9.58393 6.3213 9.52149C6.32129 9.45906 6.307 9.39727 6.27834 9.3418L5.33693 7.54688C5.29808 7.47263 5.28431 7.38755 5.29689 7.3047C5.30955 7.22196 5.34814 7.14522 5.40724 7.08595L6.59084 5.90235C6.65007 5.84308 6.72676 5.80367 6.80959 5.79102C6.89248 5.77844 6.97756 5.79303 7.05177 5.83204L8.85158 6.77442C8.90701 6.80307 8.96887 6.81736 9.03127 6.81739C9.09367 6.81739 9.1555 6.80305 9.21095 6.77442C9.43282 6.66328 9.66406 6.57058 9.90138 6.49805C9.96003 6.48172 10.014 6.45161 10.0586 6.41016C10.1033 6.36857 10.1377 6.31632 10.1582 6.2588L10.8584 4.2588C10.8853 4.1822 10.9356 4.11606 11.002 4.06934C11.0683 4.02268 11.1474 3.99753 11.2285 3.99805H12.791ZM11.9981 9.99708C11.6756 9.99098 11.3549 10.0482 11.0547 10.166C10.7546 10.2839 10.4802 10.4598 10.2481 10.6836C10.016 10.9074 9.83091 11.1751 9.70216 11.4707C9.57339 11.7664 9.50379 12.0848 9.49806 12.4072V12.4951L9.49904 12.4981C9.487 13.1487 9.73414 13.7773 10.1856 14.2461C10.6371 14.7149 11.2564 14.9857 11.9072 14.9981H11.9971C12.648 15.0101 13.2773 14.7633 13.7461 14.3115C14.2149 13.8598 14.485 13.2398 14.4971 12.5889V12.4971C14.503 12.1747 14.4451 11.8539 14.3272 11.5537C14.2093 11.2537 14.0333 10.9801 13.8096 10.7481C13.5859 10.516 13.3189 10.33 13.0235 10.2012C12.7278 10.0724 12.4093 10.0029 12.0869 9.99708H11.9981Z" fill="currentColor" />
                </svg>
            </button>

            {isOpen && (
                <div className="table_column_visibility_menu__dropdown">
                    {onReset && (
                        <>
                            <div
                                className="table_column_visibility_menu__reset_item"
                                onClick={handleReset}
                            >
                                <button
                                    className="table_column_visibility_menu__reset"
                                    type="button"
                                    tabIndex={-1}
                                >
                                    Reset to Default
                                </button>
                            </div>
                            <div className="table_column_visibility_menu__divider" />
                        </>
                    )}
                    <div className="table_column_visibility_menu__columns">
                        {table.getAllLeafColumns().map((column) => {
                            if (!column.columnDef.header) {
                                return null;
                            }

                            const isVisible = column.getIsVisible();
                            const isDisabled = isVisible && visibleColumnsCount <= 1;

                            const handleColumnToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
                                if (isDisabled) {
                                    return e;
                                }
                                column.getToggleVisibilityHandler()(e);
                                return e;
                            };

                            return (
                                <label
                                    key={column.id}
                                    className={`table_column_visibility_menu__item${isVisible ? " table_column_visibility_menu__item--active" : ""}${isDisabled ? " table_column_visibility_menu__item--disabled" : ""}`}
                                    htmlFor={`col-vis-${column.id}`}
                                >
                                    <span className="table_column_visibility_menu__item_bar" />
                                    <span className="table_column_visibility_menu__item_content">
                                        <span className="table_column_visibility_menu__item_label">{getColumnLabel(column)}</span>
                                        <input
                                            id={`col-vis-${column.id}`}
                                            type="checkbox"
                                            className="checkbox"
                                            checked={isVisible}
                                            onChange={handleColumnToggle}
                                            disabled={isDisabled}
                                        />
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default TableColumnVisibilityMenu;
